import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getRelatedVideos, getVideoById } from "@/lib/videoService";
import RelatedVideos from "./components/RelatedVideos";

export default function Detail() {
  const { id } = useParams();
  const video = getVideoById(id || "");
  const [toast, setToast] = useState("");

  useSeo({
    title: video ? `${video.title} - 芭乐视频` : "视频不存在或已下架 - 芭乐视频",
    description: video ? video.summary : "您访问的视频不存在或已下架，请返回首页浏览更多精彩内容。",
    keywords: video ? `芭乐视频,${video.title},${video.genre.join(",")},${video.region}` : "芭乐视频,视频不存在",
  });

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast(`请手动复制链接：${url}`);
    }
    setTimeout(() => setToast(""), 2600);
  };

  if (!video) {
    return (
      <div className="w-full px-4 md:px-8 py-20 flex flex-col items-center text-center">
        <span className="flex items-center justify-center w-20 h-20 rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-4xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">视频不存在或已下架</h1>
        <p className="mt-3 text-sm text-foreground-500 max-w-md">
          抱歉，您访问的内容可能已被移除或链接有误。不妨回到首页，看看最近上新的热门作品。
        </p>
        <Link
          to="/"
          className="mt-7 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
        >
          <i className="ri-home-4-line" /> 返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedVideos(video, 6);
  const metaItems = [
    { label: "类型", value: video.type },
    { label: "地区", value: video.region },
    { label: "年份", value: String(video.year) },
    { label: "状态", value: video.episodes },
  ];

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <Link to={`/category/${video.type}`} className="hover:text-primary-600 transition-colors">{video.type}</Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="truncate max-w-[180px] text-foreground-800 font-medium">{video.title}</span>
        </nav>

        <div className="relative w-full h-[240px] md:h-[340px] rounded-lg overflow-hidden bg-foreground-950">
          <img
            src={video.banner}
            alt={`${video.title}背景图`}
            title={`${video.title} - 芭乐视频`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/25" />
          <div className="absolute bottom-5 left-5 right-5 md:bottom-7 md:left-7 flex flex-wrap items-center gap-2">
            <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-label font-semibold">
              {video.type}
            </span>
            <span className="px-3 py-1 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 text-xs font-label">
              {video.episodes}
            </span>
            <span className="px-3 py-1 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 text-xs font-label">
              <i className="ri-star-fill text-accent-400" /> {video.rating.toFixed(1)}
            </span>
          </div>
        </div>

        <div className="mt-6 md:mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-1">
              <div className="w-full max-w-[280px] mx-auto lg:mx-0 aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200/70">
                <img
                  src={video.cover}
                  alt={`${video.title}封面`}
                  title={`${video.title} - 芭乐视频高清影视`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-5 rounded-lg bg-background-50 border border-background-200/70 p-5">
                <h4 className="font-heading text-sm font-semibold text-foreground-900">
                  <Link to={`/category/${video.type}`} className="hover:text-primary-600 transition-colors">基本信息</Link>
                </h4>
                <dl className="mt-4 space-y-3 text-sm">
                  {metaItems.map((item) => (
                    <div key={item.label} className="flex items-center justify-between gap-3">
                      <dt className="text-foreground-500">{item.label}</dt>
                      <dd className="text-foreground-900 font-medium">{item.value}</dd>
                    </div>
                  ))}
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">评分</dt>
                    <dd className="flex items-center gap-1 font-label font-medium text-accent-700">
                      <i className="ri-star-fill" /> {video.rating.toFixed(1)}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-semibold">
                  {video.type}
                </span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label">
                  {video.episodes}
                </span>
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-label">
                  热度 {(video.heat / 1000).toFixed(1)}k
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                {video.title}
              </h1>

              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{video.summary}</p>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                {video.genre.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${video.type}?genre=${encodeURIComponent(g)}`}
                    className="px-3 py-1.5 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-not-allowed opacity-90"
                  title="演示版本暂不提供播放功能"
                >
                  <i className="ri-play-circle-line text-lg" /> 立即观看（敬请期待）
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-secondary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-secondary-600 transition-colors"
                >
                  <i className="ri-share-forward-line" /> 分享
                </button>
                <Link
                  to={`/category/${video.type}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 whitespace-nowrap hover:bg-background-100 transition-colors"
                >
                  <i className="ri-list-check" /> 返回{video.type}频道
                </Link>
              </div>

              <div className="mt-8 rounded-lg bg-background-50 border border-background-200/70 p-5 md:p-6">
                <h4 className="font-heading text-base font-semibold text-foreground-950">
                  <Link to={`/category/${video.type}`} className="hover:text-primary-600 transition-colors">剧情简介</Link>
                </h4>
                <p className="mt-3 text-sm md:text-base text-foreground-600 leading-7">{video.description}</p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5 pt-5 border-t border-background-200/70">
                  <div>
                    <h5 className="text-sm font-medium text-foreground-900">主要演员</h5>
                    <ul className="mt-2.5 space-y-1.5 text-sm text-foreground-500">
                      {video.actors.map((actor) => (
                        <li key={actor} className="flex items-center gap-2">
                          <i className="ri-user-star-line text-primary-500" /> {actor}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-foreground-900">导演 / 制作</h5>
                    <p className="mt-2.5 text-sm text-foreground-500 flex items-center gap-2">
                      <i className="ri-clapperboard-line text-primary-500" /> {video.director}
                    </p>
                    <p className="mt-2 text-sm text-foreground-500 flex items-center gap-2">
                      <i className="ri-calendar-line text-primary-500" /> 更新于 {video.updatedAt}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <RelatedVideos videos={related} />
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm animate-soft-in max-w-[90vw]">
          <span className="flex items-center gap-2"><i className="ri-checkbox-circle-line text-primary-400" /> {toast}</span>
        </div>
      ) : null}
    </div>
  );
}